import { inject, Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction, UserStoreService } from '@mediahaven/shared';
import {
    ApiFragment,
    Family,
    Mh2Entity,
    NavigationLink,
    RecordTypeStructure,
    TaskKey,
    User
} from '@stylehaven/components';
import { EMPTY, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ActionService } from '../../abstract/action.service.interface';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { PublishActionSandbox } from '@common/sandboxes/actions/publish-action/publish-action.sandbox';
import { SubmitActionSandbox } from '@common/sandboxes/actions/submit-action/submit-action.sandbox';

@Injectable({ providedIn: 'root' })
export class SubmitActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.SUBMIT_FOR_EVALUATION;

    // Services
    private readonly userStoreService: UserStoreService = inject(UserStoreService);
    private readonly submitActionSandbox: SubmitActionSandbox = inject(SubmitActionSandbox);
    private readonly publishActionSandbox: PublishActionSandbox = inject(PublishActionSandbox);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /*  Submit
     *  Available when:
     *   * It is submittable (set by ContextMiddleware)
     *   * It is NOT deleted
     *   * It is not a representation
     *   * It is in concept
     *
     *  If has executable Correction task
     *  Disabled when:
     *   * Not all required fields set.
     *   * Is in a Mh2Collection
     *   * Is not a top level record
     *
     *  If does not have executable Correction task
     *  Disabled when:
     *   * Has non-editable status
     *   * Is pending in another workflow
     *   * Not all required fields set.
     *   * Is in a Mh2Collection
     *   * Is not a top level record
     *   * Is a Fragment
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return this.getCurrentUser().pipe(map(user => this.userActionBuilder(record, user)));
    }

    private getCurrentUser(): Observable<User> {
        return this.userStoreService.getCurrentUser();
    }

    public userActionBuilder(record: ApiFragment, user: User): UserAction {
        const hasSubmitRights: boolean = record.hasSubmitRights();
        const isDeleted: boolean = record.isDeleted();
        const isRepresentation: boolean = record.getRecordStructure() === RecordTypeStructure.Representation;
        const isConcept: boolean = record.isConcept();

        const hasSubmittableStatus: boolean = record.get(Family.Context, 'HasSubmittableStatus');

        const modelValid: boolean = record.hasValidModel();
        const isPending: boolean = record.hasPendingTask();
        const hasExecutableCorrectionTask: boolean =
            isPending &&
            record.getTasks().some(task => task.TypeId === TaskKey.CorrectMedia && task.AssigneeUserId === user.Id);

        const isFragment = !!record.getMainFragmentId();

        const availableConditions: boolean[] = [hasSubmitRights, !isDeleted, !isRepresentation, isConcept];
        let disableConditions: ActionDisabledCondition[];

        if (hasExecutableCorrectionTask) {
            disableConditions = [{ condition: !modelValid, tooltip: 'general.invalid-model' }];
        } else {
            disableConditions = [
                { condition: isPending, tooltip: 'general.pending-task' },
                { condition: !hasSubmittableStatus, tooltip: 'general.blocked-status' },
                { condition: !modelValid, tooltip: 'general.invalid-model' },
                { condition: isFragment, tooltip: 'submit.is-fragment' }
            ];
        }

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        // Uses same logic as publish so isPublishable is used here as well
        const canSubmit$: Observable<boolean> = from(this.publishActionSandbox.validatePublishable(record));

        return canSubmit$.pipe(switchMap(canSubmit => (canSubmit ? this.onSubmit(record, actionItemOptions) : EMPTY)));
    }

    private onSubmit(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        this.initBusyToast(record, actionItemOptions, true);

        return this.submitActionSandbox.submitRecordForEvaluation(record);
    }

    // #region Labels

    public override getLabelKey(target: ApiFragment): string {
        let label: string = this.translate('actions.submit');

        if (target.isNewCollection()) {
            label = this.translate('actions.intellectual.submit');
        }

        return label;
    }

    public override getIcon(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return target.isConcept() && actionItemOptions.withIcon ? 'clipboard-check' : '';
    }

    public override getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.submit-for-evaluation';
    }

    public override getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public override getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public override getSuccessToastLink(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): NavigationLink | undefined {
        return undefined;
    }

    public override getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }

    // #endregion
}
